import React, { useState } from "react";
import { Group } from "../../types/group";

type Props = { group: Group };

export const GroupCard: React.FC<Props> = ({ group }) => {
  const CardImage: React.FC<{ alt: string; candidates: string[]; className?: string }> = ({ alt, candidates, className }) => {
    const [idx, setIdx] = useState(0);
    const src = candidates[idx];
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
        onError={() => {
          if (idx < candidates.length - 1) setIdx(idx + 1);
        }}
      />
    );
  };

  return (
    <div className="group" aria-label={group.title}>
      {/* Subtle gradient border wrapper */}
      <div className="relative rounded-3xl">
        <a
          href={group.pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-react-snap="no"
          aria-label={`${group.title} – PDF öffnen`}
          className="block focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ergo-400 rounded-3xl"
        >
          <article
            className="relative rounded-3xl overflow-hidden bg-transparent shadow-[0_10px_30px_rgba(0,0,0,0.06)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.10)] transition-all duration-500 ease-out will-change-transform hover:-translate-y-1"
          >
            <div className="relative">
              {/* Cover image (fallback to derived path) */}
              <div className="relative w-full overflow-hidden">
                <CardImage
                  alt={group.description || group.title}
                  className="block mx-auto w-full h-auto object-contain"
                  candidates={[
                    ...(group.coverImage ? [group.coverImage] : []),
                    `/images/gruppen/${group.id}-cover.jpg`,
                    `/images/gruppen/${group.id}-cover.jpeg`,
                    `/images/gruppen/${group.id}-cover-1.jpg`,
                    `/images/gruppen/${group.id}-cover-1.jpeg`,
                  ]}
                />
              </div>

              {/* Animated shine on hover */}
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute -left-1/3 top-0 h-full w-1/3 bg-gradient-to-r from-white/0 via-white/50 to-white/0 transform -skew-x-12 translate-x-[-120%] group-hover:translate-x-[220%] transition-transform duration-700 ease-out" />
              </div>

              {/* Inner overlay: smaller card overlapping the cover (fixed ratio) */}
              <div
                className={`pointer-events-none absolute w-[70%] ${group.animationDirection === 'top' ? 'top-4 left-4' : 'bottom-4 right-4'
                  }`}
              >
                <div
                  className={`relative rounded-xl shadow-xl transform rotate-2 scale-[0.98] group-hover:translate-y-0 group-hover:rotate-0 group-hover:scale-100 group-focus-within:translate-y-0 group-focus-within:rotate-0 group-focus-within:scale-100 transition-transform duration-500 ease-out ${group.animationDirection === 'top' ? '-translate-y-6' : 'translate-y-6'
                    }`}
                >
                  <div className="relative overflow-hidden rounded-xl p-0 bg-transparent">
                    <CardImage
                      alt={`${group.title} – Details`}
                      className="block mx-auto w-full h-auto object-contain"
                      candidates={[
                        ...(group.innerImage ? [group.innerImage] : []),
                        `/images/gruppen/${group.id}-inner.jpg`,
                        `/images/gruppen/${group.id}-inner.jpeg`,
                        `/images/gruppen/${group.id}-inner-2.jpg`,
                        `/images/gruppen/${group.id}-inner-2.jpeg`,
                      ]}
                    />
                  </div>
                </div>
              </div>
            </div>

            <header className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 leading-snug">{group.title}</h3>
            </header>
          </article>
        </a>
      </div>
    </div>
  );
};


